import { Plus } from "lucide-react";
import * as React from "react";
import { useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import Avatar from "../social/Avatar";
import { AnimatedKnot, KnotMotion } from "../ui/Knot";
import RowMenu from "../ui/RowMenu";
import NavCord from "./NavCord";
import QuickLog from "./QuickLog";
import ThemeToggle from "./ThemeToggle";

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // The mark ties itself when the app opens and takes a tug on hover.
  const knot = useRef<KnotMotion>(null);
  const tabs = useRef<HTMLElement>(null);
  const [logging, setLogging] = useState(false);
  // The account button, while its menu is open.
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    // Signed in, the header is the app's bar, with a cord along its foot
    // and a knot under the tab you're on.
    <header className={user ? "header header-app" : "header"}>
      <NavLink
        to="/"
        className="brand"
        onMouseEnter={() => knot.current!.tug()}
      >
        <AnimatedKnot
          ref={knot}
          className="brand-knot"
          x0={10}
          x1={54}
          tieOnMount
        />
        Khipur
      </NavLink>

      <nav className="nav" aria-label="Main" ref={tabs}>
        {user && (
          <NavLink to="/" end>
            Home
          </NavLink>
        )}
        <NavLink to="/browse">Browse</NavLink>
        {user && (
          <>
            <NavLink to="/library">Library</NavLink>
            <NavLink to="/lists">Lists</NavLink>
            <NavLink to="/feed">Feed</NavLink>
          </>
        )}
      </nav>

      <div className="header-right">
        {user && (
          <button
            type="button"
            className="btn btn-primary btn-sm header-log"
            aria-label="Log something"
            onClick={() => setLogging(true)}
          >
            <Plus size="1em" aria-hidden="true" strokeWidth={2.75} />
            <span className="header-log-label">Log something</span>
          </button>
        )}
        <ThemeToggle />
        {user ? (
          <button
            type="button"
            className="header-me"
            aria-label={`Account: @${user.username}`}
            aria-haspopup="menu"
            aria-expanded={menu !== null}
            onClick={(e) => setMenu(e.currentTarget)}
          >
            <Avatar user={user} />
          </button>
        ) : (
          <>
            <NavLink to="/login" className="btn btn-sm btn-ghost">
              Log in
            </NavLink>
            <NavLink to="/signup" className="btn btn-sm btn-primary">
              Sign up
            </NavLink>
          </>
        )}
      </div>

      {user && <NavCord tabs={tabs} />}
      {logging && <QuickLog onClose={() => setLogging(false)} />}
      {user && menu && (
        <RowMenu
          x={menu.getBoundingClientRect().left}
          y={menu.getBoundingClientRect().bottom + 6}
          label="Account"
          returnFocus={menu}
          onClose={() => setMenu(null)}
          items={[
            { kind: "label", label: `@${user.username}` },
            {
              kind: "item",
              label: "Profile",
              onSelect: () => navigate(`/u/${user.username}`),
            },
            {
              kind: "item",
              label: "Settings",
              onSelect: () => navigate("/settings"),
            },
            { kind: "separator" },
            { kind: "item", label: "Log out", onSelect: onLogout },
          ]}
        />
      )}
    </header>
  );
};

export default Header;
